import React from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';

interface ApiValue {
  code: string;
  description: string;
}

interface ApiParameter {
  name: string;
  required: boolean;
  type: string;
  description: string;
  values: ApiValue[];
}

interface ApiResponse {
  description: string;
  parameters: ApiParameter[];
}

export const IaCApiFilter = ({
  onChange,
  errors,
  rawErrors,
  required,
  help,
  disabled,
  schema: { title = 'Scope', description },
}: FieldExtensionComponentProps<string>) => {
  const [selectedScope, setSelectedScope] = React.useState<string>('');

  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  const url = config.getString('backend.baseUrl');
  const api = `${url}/api/proxy/nestle/iac/api/name/reference`;

  const { value, loading, error } = useAsync(async (): Promise<string[]> => {
    try {
      console.log('IaCApiFilter: Fetching from API:', api);
      const res = await fetchApi.fetch(api, {
        headers: {
          Accept: 'application/json',
        },
      });
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      
      const data: ApiResponse = await res.json();
      console.log('IaCApiFilter: API Response:', data);
      
      // Check if the expected data structure exists
      if (!data || !data.parameters || !Array.isArray(data.parameters)) {
        console.error('IaCApiFilter: Unexpected data structure - parameters array not found:', data);
        return [];
      }
      
      // Find the scopes parameter
      const scopesParam = data.parameters.find((param: ApiParameter) => param.name === 'scopes');
      
      if (!scopesParam || !scopesParam.values || !Array.isArray(scopesParam.values)) {
        console.error('IaCApiFilter: Scopes parameter not found or invalid structure:', scopesParam);
        return [];
      }

      // Extract the scope codes from the values array
      const scopes = scopesParam.values.map((value: ApiValue) => value.code);
      console.log('IaCApiFilter: Parsed scopes:', scopes);
      return scopes;
    } catch (err) {
      console.error('IaCApiFilter: Error fetching scopes:', err);
      throw err;
    }
  });

  const scopes = value
    ? value.map((item: string) => ({ label: item, value: item }))
    : [];

  const updateScope = (scope: string) => {
    setSelectedScope(scope);
    onChange(scope);
  };

  // Show loading state
  if (loading) {
    return (
      <ScaffolderField
        rawErrors={rawErrors}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <InputLabel>{title}</InputLabel>
        <Input
          id="scope-input"
          placeholder="Loading scopes..."
          disabled={true}
        />
      </ScaffolderField>
    );
  }

  // Show error state
  if (error) {
    console.error('IaCApiFilter: Error state:', error);
    return (
      <ScaffolderField
        rawErrors={[...(rawErrors || []), `Failed to load scopes: ${error.message}`]}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <InputLabel>{title}</InputLabel>
        <Input
          id="scope-input"
          onChange={e => updateScope(e.target.value)}
          value={selectedScope}
          placeholder="Enter scope manually (API unavailable)"
        />
      </ScaffolderField>
    );
  }

  return (
    <ScaffolderField
      rawErrors={rawErrors}
      errors={errors}
      required={required}
      help={help}
      rawDescription={description}
      disabled={disabled}
    >
      {scopes?.length ? (
        <FormControl>
          <InputLabel>{title}</InputLabel>
          <Select
            value={selectedScope}
            onChange={e => updateScope(e.target.value as string)}
            disabled={scopes.length === 1}
            displayEmpty
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 400,
                },
              },
            }}
          >
            {scopes.map(scope => (
              <MenuItem key={scope.value} value={scope.value}>
                {scope.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <>
          <InputLabel>{title}</InputLabel>
          <Input
            id="scope-input"
            onChange={e => updateScope(e.target.value)}
            value={selectedScope}
            placeholder="No scopes loaded - enter manually"
          />
        </>
      )}
    </ScaffolderField>
  );
};
